import * as React from "react"

import { Slot } from "@radix-ui/react-slot"

import { cn } from "../../cn"
import { Tooltip } from "../Tooltip/Tooltip"

export const BREADCRUMB_TOOLTIP_CLASS_NAME =
  "max-w-[var(--radix-tooltip-content-available-width)] whitespace-normal break-words"

export interface TruncatedTextProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /**
   * The label. Clipped to one line, and shown whole in the tooltip. Rich
   * content is allowed; the tooltip then carries the text it renders to.
   */
  children: React.ReactNode
  /** Which side the tooltip opens on. Defaults to the house default (top). */
  side?: "top" | "right" | "bottom" | "left"
  /** Extra classes for the tooltip, such as a viewport-constrained width. */
  tooltipClassName?: string
  /** Use a single child element as the clipped text and tooltip trigger, preserving native link focus. */
  asChild?: boolean
}

// An ancestor that already takes focus names the clipped text for assistive
// tech, so the text never becomes a second, nested tab stop inside it. A row or
// grid cell with any tabindex counts, including a roving `-1`: a clickable
// table row treats a `role="button"` descendant as its own control and stops
// handling the click (src/packages/ui/src/components/Table/TableInternals.tsx).
const INTERACTIVE_ANCESTOR_SELECTOR = [
  "a[href]",
  "button",
  "summary",
  "label",
  "[role=button]",
  "[role=link]",
  "[role=menuitem]",
  "[role=menuitemcheckbox]",
  "[role=menuitemradio]",
  "[role=option]",
  "[role=tab]",
  "[role=treeitem]",
  "[role=checkbox]",
  "[role=radio]",
  "[role=switch]",
  '[tabindex]:not([tabindex="-1"])',
  "tr[tabindex]",
  "[role=row][tabindex]",
  "[role=gridcell][tabindex]",
].join(",")

/**
 * Single-line content that ends in an ellipsis when it outgrows its box, and
 * reveals the whole string in a tooltip on hover or keyboard focus. While the
 * text is clipped it becomes a tab stop of its own, unless a focusable
 * ancestor or a native link child already is one; pass `tabIndex={-1}` to opt
 * out. Short values stay quiet and out of the tab order.
 *
 * Sized by its container: give the parent a width (and `min-w-0` inside a flex
 * or grid item, whose automatic minimum size otherwise refuses to shrink) and
 * this fills it.
 */
const TruncatedText = ({
  children,
  className,
  side,
  tooltipClassName,
  asChild = false,
  tabIndex,
  ...props
}: TruncatedTextProps) => {
  const textRef = React.useRef<HTMLSpanElement | null>(null)
  const Component = asChild ? Slot : "span"
  // The text the box had to cut off, or null while it all fits. Read off the
  // node rather than from `children`, so rich content reveals the same string
  // the user sees clipped.
  const [clippedText, setClippedText] = React.useState<string | null>(null)
  const [hasVisibleWidth, setHasVisibleWidth] = React.useState(true)
  const [isInsideControl, setIsInsideControl] = React.useState(false)

  React.useLayoutEffect(() => {
    const node = textRef.current
    if (!node) {
      return
    }
    // Both widths are rounded to integers, so a subpixel layout can report a
    // scroll width one larger than the client width with nothing clipped.
    const measure = () => {
      setIsInsideControl(
        node.parentElement?.closest(INTERACTIVE_ANCESTOR_SELECTOR) != null,
      )
      const visible = node.clientWidth > 1
      setHasVisibleWidth(visible)
      setClippedText(
        visible && node.scrollWidth > node.clientWidth + 1
          ? node.textContent
          : null,
      )
    }
    measure()
    // Catches the box changing under fixed text — a column resize, a sidebar
    // opening. Text or styling changing under a fixed box re-runs the effect
    // instead, since neither necessarily resizes the span.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure)
    observer?.observe(node)
    // A child component can load its label without rerendering this component or changing the clipped box's size.
    const textObserver = new MutationObserver(measure)
    textObserver.observe(node, {
      childList: true,
      characterData: true,
      subtree: true,
    })
    // A row can become clickable, or stop being, when its data refreshes
    // without this text changing, so the ancestors' focusability is watched.
    const ancestorObserver = new MutationObserver(measure)
    for (
      let ancestor = node.parentElement;
      ancestor;
      ancestor = ancestor.parentElement
    ) {
      ancestorObserver.observe(ancestor, {
        attributes: true,
        attributeFilter: ["tabindex", "role", "href"],
      })
    }
    return () => {
      observer?.disconnect()
      textObserver.disconnect()
      ancestorObserver.disconnect()
    }
  }, [children, className, asChild])

  const resolvedTabIndex = !hasVisibleWidth
    ? -1
    : clippedText === null
      ? tabIndex === 0
        ? undefined
        : tabIndex
      : (tabIndex ?? (asChild || isInsideControl ? undefined : 0))
  // A tab stop needs a widget role. The text acts as the tooltip's trigger, the
  // same role Radix gives a TooltipTrigger. A native link or button child, or
  // one that names its own role, keeps it, so no `role` key is passed at all:
  // an undefined one would override the child's.
  const childProps: { role?: string; href?: string } =
    asChild && React.isValidElement(children)
      ? (children.props as { role?: string; href?: string })
      : {}
  const childIsControl =
    asChild &&
    React.isValidElement(children) &&
    (children.type === "a" ||
      children.type === "button" ||
      childProps.role !== undefined ||
      childProps.href !== undefined)
  const roleProps =
    props.role === undefined && !childIsControl && resolvedTabIndex === 0
      ? { role: "button" }
      : {}

  return (
    <Tooltip
      className={tooltipClassName}
      content={clippedText}
      side={side}
      openOnInteraction={clippedText !== null}
    >
      <Component
        ref={textRef}
        className={cn("block min-w-0 truncate", className)}
        {...props}
        {...roleProps}
        tabIndex={resolvedTabIndex}
      >
        {children}
      </Component>
    </Tooltip>
  )
}

export { TruncatedText }
